import React, { useState } from 'react';

import {
  ChecklistRtl,
  Download,
  Edit,
  NavigateNext,
  Send,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { APIEndpoint } from 'common/enum';
import { popupNotification } from 'notifications/ToastNotification';

interface PublicSigneesSidebarProps {
  onFinishAndSend: () => void;
  onSignClick: () => void;
  userSignature: {
    type: 'draw' | 'type';
    value: string;
    typedText?: string;
  } | null;
  onEditSignature: () => void;
  onDownload: () => void;
  onNavigateToNextField?: () => void;
  areAllFieldsCompleted?: boolean;
  fieldCompletionStatus?: { completed: number; total: number };
  signees: Array<{
    name: string;
    email: string;
    color: string;
    signeeType?: string;
    level?: number;
    fields: Array<{
      type: string;
      label: string;
    }>;
  }>;
  pdfFile: string | null;
  fields: any[];
  contractDetails?: {
    levels: number;
    yearToRetain: number;
    creatorRole: string;
    formData?: any[];
    levelData?: Array<{
      level: number;
      signees: Array<{
        name: string;
        email: string;
        signeeType: string;
        color: string;
        level: number;
        fields: Array<{
          type: string;
          label: string;
        }>;
      }>;
    }>;
  };
  pdfFilePath?: string;
  generatePdfWithFields?: () => Promise<Uint8Array>;
  uuid?: string;
  folderId?: string | null;
  signaturePositions?: Array<{
    id?: string;
    page: number;
    x: number;
    y: number;
    signed?: boolean;
    signatureUrl?: string;
    signeeName?: string;
    signeeEmail?: string;
    signeeType?: string;
    source?: 'new' | 'loaded';
    signeeId?: string;
    fieldType?: 'signature' | 'initials'; // Add fieldType to distinguish between signature and initials
  }>;
  generatePayloadWithSigneeUuids?: () => {
    signees: Array<{
      signeeId: string;
      signeeName: string;
      signeeEmail: string;
      signeeType: string;
      fields: any[];
    }>;
    totalFields: number;
    contractDetails?: any;
  };
  contractId?: string;
  emailId?: string;
  fullFilePath?: string;
  isPdfLoaded?: boolean;
}

const PublicSigneesSidebar: React.FC<PublicSigneesSidebarProps> = ({
  onFinishAndSend,
  onSignClick,
  userSignature,
  onEditSignature,
  onDownload,
  onNavigateToNextField,
  areAllFieldsCompleted = false,
  fieldCompletionStatus = { completed: 0, total: 0 },
  signees,
  pdfFile,
  fields,
  contractDetails,
  pdfFilePath,
  generatePdfWithFields,
  uuid, //same as signerId
  folderId,
  signaturePositions = [],
  generatePayloadWithSigneeUuids,
  contractId,
  emailId, // email of the signer
  fullFilePath,
  isPdfLoaded = false,
}) => {
  // Get user data from auth state

  // Add state for success dialog and loading
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');

  // Validation function to check if all signature fields are completed
  const validateAllFieldsCompleted = () => {
    if (!signaturePositions || signaturePositions.length === 0) {
      return {
        isValid: false,
        message: 'No signature fields found on the document.',
      };
    }

    // Get the current signee ID (either from uuid prop or from signaturePositions)
    const currentSigneeId =
      uuid || signaturePositions.find((pos) => pos.signeeId)?.signeeId;

    if (!currentSigneeId) {
      return {
        isValid: false,
        message: 'Unable to identify the current signee.',
      };
    }

    // Only check fields assigned to the current signee
    const currentSigneeFields = signaturePositions.filter(
      (pos) =>
        pos.signeeId === currentSigneeId &&
        (pos.fieldType === 'signature' || pos.fieldType === 'initials')
    );

    if (currentSigneeFields.length === 0) {
      return { isValid: true, message: '' }; // No fields assigned to this signee
    }

    const unsignedFields = currentSigneeFields.filter((pos) => !pos.signed);

    if (unsignedFields.length > 0) {
      const fieldTypes = unsignedFields.map((field) =>
        field.fieldType === 'initials' ? 'initials' : 'signature'
      );
      const uniqueTypes = Array.from(new Set(fieldTypes));
      const typeText =
        uniqueTypes.length === 1 ? uniqueTypes[0] : uniqueTypes.join(' and ');

      return {
        isValid: false,
        message: `You still have ${unsignedFields.length} unsigned ${typeText} field${unsignedFields.length > 1 ? 's' : ''} remaining.`,
      };
    }

    return { isValid: true, message: '' };
  };

  // Custom upload function for SigneesSidebar
  const uploadContractFile = async (file: File) => {
    try {
      const fileName = pdfFilePath;

      const { data } = await axios.post(
        APIEndpoint.uploadFile,
        {
          fileName: fileName,
          uploadedBy: emailId,
          docType: 'contract',
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const uploadUrl = data.upload_url.url;
      const formData = new FormData();

      if (data.upload_url.fields) {
        Object.entries(data.upload_url.fields).forEach(([k, v]: any) => {
          formData.append(k, v);
        });
      }

      formData.append('file', file);

      await axios.post(uploadUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // popupNotification(
      //   'Contract uploaded successfully to Contracts folder',
      //   true
      // );
      setShowSuccessDialog(true);
    } catch (error) {
      console.error('Error uploading contract:', error);
      popupNotification('Error uploading contract. Please try again.', false);
    } finally {
      setIsUploading(false);
    }
  };

  // Mock signees if none provided
  const handleFinishAndSend = async () => {
    // Validate that all signature fields are completed
    const validation = validateAllFieldsCompleted();
    if (!validation.isValid) {
      setValidationMessage(validation.message);
      setShowValidationDialog(true);
      return;
    }

    try {
      let contractData;
      const newContractSignedData = {
        contractId: contractId,
        contractLocation: fullFilePath,
        email: emailId,
        signeeId: uuid,
        httpMethod: 'LOCAL',
        actionName: 'DigitallySignContract',
      };
      // Generate the final PDF with signatures if the function is available
      if (generatePdfWithFields) {
        const pdfBytes = await generatePdfWithFields();

        // Convert PDF bytes to File object
        const pdfBlob = new Blob([pdfBytes as BlobPart], {
          type: 'application/pdf',
        });
        const contractFileName = '';
        const pdfFile = new File([pdfBlob], contractFileName, {
          type: 'application/pdf',
        });

        // Calculate the contract location first
        const contractLocation = pdfFilePath;
        // const contractLocation = `${'74d65aea-4f41-41eb-9576-92c7666b1499'}/${folderId ? folderId + '/' : ''}Home/Contracts/${contractFileName}`;

        // Update contract data with the correct location

        // function to save the contract json payload
        const saveContractPayload = async (contractData: any) => {
          try {
            setIsUploading(true);
            const response = await axios.post(
              APIEndpoint.signContract,
              contractData,
              {
                headers: {
                  'Content-Type': 'application/json',
                },
              }
            );

            if (response.status === 200) {
              popupNotification('Contract saved successfully', true);
            } else {
              // popupNotification('Error saving contract payload', false);
            }
          } catch (error) {
            console.error('Error saving contract payload:', error);
            // popupNotification('Error saving contract payload', false);
          }
        };

        // Save the contract JSON payload with correct location
        if (newContractSignedData) {
          await saveContractPayload(newContractSignedData);
        }

        // Upload the contract using custom function
        if (uuid) {
        }
        await uploadContractFile(pdfFile);
      }

      // Call the original onFinishAndSend function
      onFinishAndSend();
    } catch (error) {
      console.error('Error in handleFinishAndSend:', error);
      popupNotification('Error uploading contract. Please try again.', false);
    }
  };

  return (
    <>
      {/* Full-page overlay loader */}
      {isUploading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <CircularProgress size={60} sx={{ mb: 3, color: 'white' }} />
          <Typography variant="h6" color="white" textAlign="center" mb={2}>
            Uploading Contract...
          </Typography>
          <Typography
            variant="body1"
            color="white"
            textAlign="center"
            sx={{ opacity: 0.8 }}
          >
            Please wait while we process and upload your contract
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          width: 300,
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          borderLeft: '2px solid #dee2e6',
          padding: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          position: 'relative',
          height: 'calc(100vh - 80px)', // Fixed height to enable scrolling
          overflow: 'hidden', // Prevent outer container from scrolling
          boxShadow: 'inset 2px 0 8px rgba(0,0,0,0.05)',
        }}
      >
        {/* Overlay Loader for sidebar only */}
        {isUploading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              borderRadius: 1,
            }}
          >
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              Uploading contract...
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
              sx={{ mt: 1 }}
            >
              Please wait while we process your contract
            </Typography>
          </Box>
        )}
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            fontWeight: 700,
            color: '#2c3e50',
            fontSize: '1.25rem',
            borderBottom: '2px solid #3498db',
            paddingBottom: 1,
            marginBottom: 2,
          }}
        >
          Signees & Fields
        </Typography>

        {/* Scrollable content area */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" gutterBottom>
            Signee Email: {emailId}
          </Typography>
        </Box>
        <Box
          sx={{
            // flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            pr: 1,
            mr: -1,
            '&::-webkit-scrollbar': {
              width: '10px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%)',
              borderRadius: '8px',
              border: '1px solid #dee2e6',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'linear-gradient(180deg, #6c757d 0%, #495057 100%)',
              borderRadius: '8px',
              border: '1px solid #495057',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'linear-gradient(180deg, #495057 0%, #343a40 100%)',
            },
          }}
        >
          {/* Signees and their fields */}

          <Divider
            sx={{
              my: 2,
              borderColor: '#dee2e6',
              borderWidth: '1px',
              opacity: 0.8,
            }}
          />
          <Typography
            variant="body2"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: '#495057',
              fontSize: '0.95rem',
              letterSpacing: '0.5px',
            }}
          >
            <span style={{ fontWeight: 'bold', color: '#2c3e50' }}>
              Required Fields:
            </span>
          </Typography>

          {/* User signature section */}
          <Card
            sx={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
              border: '1px solid #e9ecef',
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
                transform: 'translateY(-2px)',
              },
            }}
          >
            <CardContent sx={{ padding: 2.5 }}>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  color: '#2c3e50',
                  fontSize: '1.1rem',
                  marginBottom: 2,
                }}
              >
                Your Signature
              </Typography>
              {userSignature ? (
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      border: '2px solid #e9ecef',
                      borderRadius: '8px',
                      padding: 1,
                      background: '#ffffff',
                      boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)',
                    }}
                  >
                    <img
                      src={userSignature.value}
                      alt="User signature"
                      style={{
                        maxWidth: '100%',
                        maxHeight: 100,
                        display: 'block',
                        borderRadius: '4px',
                      }}
                    />
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={onEditSignature}
                    startIcon={<Edit />}
                    sx={{
                      mt: 2,
                      borderColor: '#3498db',
                      color: '#3498db',
                      fontWeight: 500,
                      fontSize: '0.8rem',
                      padding: '6px 12px',
                      '&:hover': {
                        borderColor: '#2980b9',
                        backgroundColor: 'rgba(52, 152, 219, 0.04)',
                      },
                    }}
                  >
                    Edit Signature
                  </Button>
                </Box>
              ) : null}
            </CardContent>
          </Card>
        </Box>

        {/* Progress Indicator */}
        {fieldCompletionStatus.total > 0 && (
          <Box sx={{ mt: 2, mb: 1 }}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}
            >
              <Typography variant="body2" color="text.secondary">
                Signature Progress
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight="bold"
              >
                {fieldCompletionStatus.completed}/{fieldCompletionStatus.total}
              </Typography>
            </Box>
            <Box
              sx={{
                width: '100%',
                height: 8,
                backgroundColor: '#e9ecef',
                borderRadius: 4,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: `${(fieldCompletionStatus.completed / fieldCompletionStatus.total) * 100}%`,
                  height: '100%',
                  backgroundColor: areAllFieldsCompleted
                    ? 'linear-gradient(90deg, #27ae60 0%, #2ecc71 100%)'
                    : 'linear-gradient(90deg, #3498db 0%, #2980b9 100%)',
                  transition: 'width 0.5s ease-in-out',
                }}
              />
            </Box>
          </Box>
        )}

        {/* Action buttons */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          {onNavigateToNextField && (
            <Button
              variant="outlined"
              onClick={onNavigateToNextField}
              disabled={areAllFieldsCompleted}
              fullWidth
              startIcon={
                areAllFieldsCompleted ? <ChecklistRtl /> : <NavigateNext />
              }
              sx={{
                borderColor: areAllFieldsCompleted ? '#27ae60' : '#f39c12',
                color: areAllFieldsCompleted ? '#27ae60' : '#f39c12',
                fontWeight: 500,
                textTransform: 'none',
                fontSize: '0.85rem',
                padding: '8px 16px',
                borderRadius: '6px',
                borderWidth: '2px',
                backgroundColor: areAllFieldsCompleted
                  ? 'rgba(39, 174, 96, 0.1)'
                  : 'transparent',
                '&:hover': {
                  borderColor: areAllFieldsCompleted ? '#27ae60' : '#e67e22',
                  backgroundColor: areAllFieldsCompleted
                    ? 'rgba(39, 174, 96, 0.15)'
                    : 'rgba(243, 156, 18, 0.04)',
                  borderWidth: '2px',
                },
                '&:disabled': {
                  borderColor: '#27ae60',
                  color: '#27ae60',
                  backgroundColor: 'rgba(39, 174, 96, 0.1)',
                },
              }}
            >
              {areAllFieldsCompleted
                ? `All Fields Completed (${fieldCompletionStatus.completed}/${fieldCompletionStatus.total})`
                : `Next Field (${fieldCompletionStatus.completed}/${fieldCompletionStatus.total})`}
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleFinishAndSend}
            disabled={!userSignature}
            fullWidth
            startIcon={<Send />}
            sx={{
              background: areAllFieldsCompleted
                ? 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)'
                : 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '0.9rem',
              padding: '10px 20px',
              borderRadius: '8px',
              boxShadow: areAllFieldsCompleted
                ? '0 3px 8px rgba(46, 204, 113, 0.3)'
                : '0 3px 8px rgba(52, 152, 219, 0.3)',
              '&:hover': {
                background: areAllFieldsCompleted
                  ? 'linear-gradient(135deg, #229954 0%, #27ae60 100%)'
                  : 'linear-gradient(135deg, #2980b9 0%, #1f5f8b 100%)',
                boxShadow: areAllFieldsCompleted
                  ? '0 4px 12px rgba(46, 204, 113, 0.4)'
                  : '0 4px 12px rgba(52, 152, 219, 0.4)',
              },
              '&:disabled': {
                background: 'linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%)',
                color: '#adb5bd',
                boxShadow: 'none',
              },
            }}
          >
            {areAllFieldsCompleted ? '🎉 Complete & Send' : 'Finish & Send'}
          </Button>
          <Button
            variant="outlined"
            onClick={onDownload}
            disabled={!userSignature}
            fullWidth
            startIcon={<Download />}
            sx={{
              borderColor: '#3498db',
              color: '#3498db',
              fontWeight: 500,
              textTransform: 'none',
              fontSize: '0.85rem',
              padding: '8px 16px',
              borderRadius: '6px',
              borderWidth: '2px',
              '&:hover': {
                borderColor: '#2980b9',
                backgroundColor: 'rgba(52, 152, 219, 0.04)',
                borderWidth: '2px',
              },
              '&:disabled': {
                borderColor: '#e9ecef',
                color: '#adb5bd',
              },
            }}
          >
            Download PDF
          </Button>
        </Box>
      </Box>

      {/* Success Dialog */}
      <Dialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', color: 'success.main' }}>
          ✅ Contract Uploaded Successfully!
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ textAlign: 'center', mb: 2 }}>
            Thank you for signing the contract. It has been routed for further
            signatures.
          </DialogContentText>
          <DialogContentText
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center' }}
          >
            The contract is now ready for signing and can be accessed by all
            parties involved.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            onClick={() => setShowSuccessDialog(false)}
            variant="contained"
            color="primary"
            sx={{ minWidth: 120 }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Validation Dialog */}
      <Dialog
        open={showValidationDialog}
        onClose={() => setShowValidationDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', color: 'warning.main' }}>
          ⚠️ Incomplete Signature Fields
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ textAlign: 'center', mb: 2 }}>
            {validationMessage}
          </DialogContentText>
          <DialogContentText
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center' }}
          ></DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            onClick={() => setShowValidationDialog(false)}
            variant="contained"
            color="primary"
            sx={{ minWidth: 120 }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PublicSigneesSidebar;
